<template>
  <div class="h-full overflow-y-auto p-4 md:p-8">
    <div class="max-w-6xl mx-auto">
      <!-- grape focus banner -->
      <div v-if="focusGrape" class="panel px-4 py-2.5 flex items-center gap-3 flex-wrap mb-5">
        <span class="w-2.5 h-2.5 rounded-full shrink-0" :style="{ background: berryColor(focusGrape.color) }" />
        <div>
          <p class="text-[0.65rem] uppercase tracking-[0.18em] text-gold-bright">
            {{ focusOn ? "the chemistry of" : "all compounds · selected" }}
          </p>
          <p class="font-display text-lg leading-tight">{{ focusGrape.name }}</p>
        </div>
        <button @click="focusOn = !focusOn" class="text-xs text-gold-bright hover:underline ml-auto">
          {{ focusOn ? `show all ${store.compounds.length} compounds →` : `just ${focusGrape.name}'s →` }}
        </button>
      </div>

      <div class="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 class="font-display text-3xl">The compound cellar</h2>
          <p class="text-sm text-ink-dim mt-1">
            {{ filtered.length }} molecules behind
            {{ focusGrape && focusOn ? `what you smell and taste in ${focusGrape.name}` : "everything you smell and taste in a glass" }}.
          </p>
        </div>
        <div class="flex gap-2 flex-wrap">
          <select v-model="klass" class="bg-card border border-line rounded-full px-3 py-1.5 text-sm text-ink">
            <option value="">All classes</option>
            <option v-for="c in classes" :key="c" :value="c">{{ c }}</option>
          </select>
          <select v-model="origin" class="bg-card border border-line rounded-full px-3 py-1.5 text-sm text-ink">
            <option value="">All origins</option>
            <option v-for="o in origins" :key="o" :value="o">{{ originLabel(o) }}</option>
          </select>
          <input v-model="q" placeholder="Search…"
            class="bg-card border border-line rounded-full px-3 py-1.5 text-sm text-ink placeholder:text-ink-faint w-40" />
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div v-for="c in filtered" :key="c.slug" class="panel p-4 flex flex-col gap-2">
          <div class="flex items-start justify-between gap-2">
            <h3 class="font-display text-lg leading-snug">{{ c.name }}</h3>
            <span class="chip shrink-0" :class="c.origin === 'fault' ? 'wine' : ''">{{ originLabel(c.origin) }}</span>
          </div>
          <p class="text-xs text-ink-faint -mt-1">{{ c.chemical_class }} <span v-if="c.formula">· {{ c.formula }}</span></p>
          <p class="text-sm text-gold-bright/90 italic font-display">“{{ c.aroma }}”</p>
          <p class="text-xs text-ink-dim leading-relaxed">{{ c.blurb }}</p>
          <p v-if="c.perception_threshold" class="text-[0.68rem] text-ink-faint">threshold {{ c.perception_threshold }}</p>
          <div v-if="c.grape_slugs?.length" class="flex flex-wrap gap-1 mt-auto pt-1">
            <button v-for="s in c.grape_slugs" :key="s" @click="open(s)" class="chip hover:bg-wine/15 cursor-pointer">
              {{ bySlug.get(s)?.name || s }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue"
import { store, bySlug, selectGrape, selectedGrape, berryColor } from "../store"

const q = ref(""), klass = ref(""), origin = ref("")

// a selected grape narrows the cellar to its chemistry; re-focus on new selection
const focusOn = ref(true)
const focusGrape = computed(() =>
  selectedGrape.value && !selectedGrape.value.ancestor_only ? selectedGrape.value : null)
watch(() => store.selectedSlug, () => { focusOn.value = true })

const focusSlugs = computed(() => {
  if (!focusGrape.value || !focusOn.value) return null
  const g = focusGrape.value
  const set = new Set(g.compounds || [])
  for (const c of store.compounds) if (c.grape_slugs?.includes(g.slug)) set.add(c.slug)
  return set
})

const classes = computed(() => [...new Set(store.compounds.map(c => c.chemical_class))].sort())
const origins = computed(() => [...new Set(store.compounds.map(c => c.origin))])

const filtered = computed(() => store.compounds.filter(c =>
  (!focusSlugs.value || focusSlugs.value.has(c.slug)) &&
  (!klass.value || c.chemical_class === klass.value) &&
  (!origin.value || c.origin === origin.value) &&
  (!q.value || (c.name + c.aroma + c.blurb).toLowerCase().includes(q.value.toLowerCase()))
))

const labels = { grape: "from the grape", fermentation: "from ferment", oak: "from oak", aging: "from age", fault: "fault" }
const originLabel = o => labels[o] || o

function open(slug) { if (bySlug.value.get(slug)) selectGrape(slug) }
</script>
